import { useEffect, useState } from "react";
import { Megaphone, PauseCircle, ArrowClockwise, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import client from "../../lib/api";

const fmtAgo = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
const pct = (a, b) => (b ? `${Math.round((a / b) * 100)}%` : "—");
const QUALITY = {
  good: ["Real traffic", "text-emerald-300 bg-emerald-500/10 border-emerald-500/30"],
  mixed: ["Mixed", "text-amber-300 bg-amber-500/10 border-amber-500/30"],
  junk: ["Junk — pause", "text-red-300 bg-red-500/10 border-red-500/30"],
  too_early: ["Too few clicks", "text-neutral-400 bg-white/[0.03] border-white/10"],
};

function LeakFunnel({ steps, clicks, leak }) {
  if (!steps?.length) return null;
  const max = Math.max(clicks || 0, ...steps.map((s) => s.count), 1);
  let prev = clicks || 0;
  return (
    <div className="mt-5" data-testid="ad-funnel-leak">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Where ad visitors drop off</div>
      <div className="space-y-1.5">
        <div className="flex items-center gap-3 text-xs"><div className="w-36 text-neutral-400 shrink-0">Clicked the ad</div><div className="flex-1 h-5 rounded bg-white/[0.04]"><div className="h-5 rounded bg-neutral-500/60" style={{ width: `${(clicks / max) * 100}%` }} /></div><div className="w-28 text-right text-neutral-200 tabular-nums">{clicks}</div></div>
        {steps.map((s) => {
          const drop = prev > 0 ? Math.round(((prev - s.count) / prev) * 100) : 0;
          const isLeak = s.key === leak;
          const row = (
            <div key={s.key} className={`flex items-center gap-3 text-xs ${isLeak ? "rounded-md bg-red-500/[0.06] -mx-2 px-2 py-1" : ""}`} data-testid={`ad-funnel-step-${s.key}`}>
              <div className={`w-36 shrink-0 ${isLeak ? "text-red-300 font-semibold" : "text-neutral-400"}`}>{s.label}</div>
              <div className="flex-1 h-5 rounded bg-white/[0.04]"><div className={`h-5 rounded ${isLeak ? "bg-red-400/70" : "bg-[#FF5A1F]/70"}`} style={{ width: `${(s.count / max) * 100}%` }} /></div>
              <div className="w-28 text-right tabular-nums"><span className="text-neutral-200">{s.count}</span> <span className={isLeak ? "text-red-300" : "text-neutral-500"}>−{drop}%</span></div>
            </div>
          );
          prev = s.count;
          return row;
        })}
      </div>
      {leak && <div className="text-[11px] text-red-300/90 mt-2" data-testid="ad-funnel-leak-note">Biggest leak: <b>{steps.find((s) => s.key === leak)?.label}</b> — that's the step to fix first.</div>}
    </div>
  );
}

export default function AdFunnelCard() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [labels, setLabels] = useState({});
  const [account, setAccount] = useState("");
  const load = () => client.get(`/admin/marketing/ad-funnel?days=${days}`).then((r) => setData(r.data)).catch(() => setData({ rows: [], totals: {}, steps: [] }));
  useEffect(() => { load(); }, [days]); // eslint-disable-line
  useEffect(() => { client.get("/admin/marketing/meta-ads").then((r) => { setLabels(r.data.campaign_labels || {}); setAccount(r.data.ad_account_id || ""); }).catch(() => {}); }, []);
  const saveLabel = async (camp, name) => {
    const next = { ...labels, [camp]: name };
    try { await client.put("/admin/marketing/meta-ads", { ad_account_id: account, campaign_labels: next }); setLabels(next); setEditing(null); load(); toast.success("Campaign name saved"); }
    catch { toast.error("Couldn't save"); }
  };
  const refreshNames = async () => {
    try { const { data: r } = await client.post("/admin/marketing/campaign-names/refresh"); toast[r.updated ? "success" : "info"](r.updated ? `Pulled ${r.updated} campaign names from Meta` : r.reason === "no_token" ? "Needs the Conversions API token first — name campaigns by hand with the pencil" : "Meta didn't return names (token lacks ads_read) — name them by hand with the pencil"); if (r.updated) load(); }
    catch { toast.error("Couldn't reach Meta"); }
  };
  const t = data?.totals || {};
  return (
    <div className="card-surface p-5" data-testid="ad-funnel-card">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500"><Megaphone size={13} /> Ad funnel by campaign</div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={refreshNames} className="inline-flex items-center gap-1 text-[11px] text-neutral-400 hover:text-white" data-testid="ad-funnel-refresh-names"><ArrowClockwise size={12} /> Pull names from Meta</button>
          <div className="flex gap-1">
            {[7, 30, 90].map((d) => (
              <button key={d} type="button" onClick={() => setDays(d)} className={`rounded-full px-2.5 py-1 text-[11px] ${days === d ? "bg-[#FF5A1F] text-black font-bold" : "text-neutral-400 hover:text-white border border-white/10"}`} data-testid={`ad-funnel-days-${d}`}>{d}d</button>
            ))}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-4">
        {[["Clicks", t.clicks], ["Stayed 5s+", t.engaged], ["Bounced", t.bounced], ["Signups", t.signups], ["Cards on file", t.cards_on_file], ["Paid", t.paid]].map(([l, v]) => (
          <div key={l} className="rounded-lg bg-white/[0.03] border border-white/[0.06] p-3" data-testid={`ad-funnel-total-${l.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">{l}</div>
            <div className="text-xl font-semibold text-white mt-1">{v ?? 0}</div>
          </div>
        ))}
      </div>
      {!data ? <div className="text-xs text-neutral-500">Loading…</div> : data.rows.length === 0 ? (
        <div className="text-xs text-neutral-500" data-testid="ad-funnel-empty">No tagged ad traffic yet. Clicks appear as soon as someone opens a link with <code>utm_campaign</code> (the Facebook demo ad uses <code>demo_60s</code>).</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="ad-funnel-table">
              <thead><tr className="text-[10px] uppercase tracking-widest text-neutral-500 text-left">
                <th className="py-1.5 pr-3">Campaign</th><th className="py-1.5 pr-3">Source</th><th className="py-1.5 pr-3">Quality</th><th className="py-1.5 pr-3 text-right">Clicks</th><th className="py-1.5 pr-3 text-right">Bounce</th><th className="py-1.5 pr-3 text-right">Signups</th><th className="py-1.5 pr-3 text-right">Cards</th><th className="py-1.5 pr-3 text-right">Paid</th><th className="py-1.5">Last click</th>
              </tr></thead>
              <tbody>
                {data.rows.map((r) => {
                  const [label, cls] = QUALITY[r.quality] || QUALITY.too_early;
                  const camp = r.utm_campaign || "none";
                  return (
                    <tr key={`${r.utm_source}|${r.utm_campaign}`} className="border-t border-white/[0.05] text-neutral-200 align-top" data-testid={`ad-funnel-row-${camp}`}>
                      <td className="py-2 pr-3">
                        {editing === camp ? (
                          <form onSubmit={(e) => { e.preventDefault(); saveLabel(camp, e.target.elements.name.value); }} className="flex gap-1">
                            <input name="name" defaultValue={labels[camp] || r.campaign_name || ""} placeholder="Name as shown in Ads Manager" className="rounded bg-white/5 border border-white/10 px-2 py-1 text-xs text-white w-44" autoFocus data-testid={`ad-funnel-label-input-${camp}`} />
                            <button type="submit" className="text-[11px] text-[#FF9764]" data-testid={`ad-funnel-label-save-${camp}`}>Save</button>
                          </form>
                        ) : (
                          <div>
                            <div className="flex items-center gap-1.5">
                              <span className={r.campaign_name ? "text-white font-medium" : "font-mono"} data-testid={`ad-funnel-name-${camp}`}>{r.campaign_name || r.utm_campaign || "—"}</span>
                              {r.is_meta_campaign && <button type="button" onClick={() => setEditing(camp)} className="text-neutral-500 hover:text-white" aria-label="Name this campaign" data-testid={`ad-funnel-label-edit-${camp}`}><PencilSimple size={11} /></button>}
                            </div>
                            {r.campaign_name && <div className="font-mono text-[10px] text-neutral-500">{r.utm_campaign}</div>}
                            {r.quality === "junk" && r.pause_url && (
                              <a href={r.pause_url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 rounded-full border border-red-500/40 bg-red-500/10 px-2 py-0.5 text-[10px] font-bold text-red-200 hover:bg-red-500/20" data-testid={`ad-funnel-pause-${camp}`}>
                                <PauseCircle size={11} weight="fill" /> Pause {r.campaign_name ? `"${r.campaign_name}"` : "this campaign"} in Ads Manager
                              </a>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-neutral-400">{r.utm_source || "—"}</td>
                      <td className="py-2 pr-3"><span className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-bold ${cls}`} data-testid={`ad-funnel-quality-${camp}`}>{label}</span></td>
                      <td className="py-2 pr-3 text-right">{r.clicks}</td><td className="py-2 pr-3 text-right text-neutral-400">{pct(r.bounced, r.clicks)}</td>
                      <td className="py-2 pr-3 text-right">{r.signups}</td><td className="py-2 pr-3 text-right">{r.cards_on_file}</td><td className="py-2 pr-3 text-right">{r.paid}</td>
                      <td className="py-2 text-neutral-500">{fmtAgo(r.last_click_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="text-[11px] text-neutral-500 mt-2">Bounce = left in under {data.engaged_threshold_sec || 5} seconds. "Junk" = 70%+ bounce on 5+ clicks — the pause link opens that exact campaign in Ads Manager (select it → Pause). Source <code>an</code> = Audience Network.</div>
          </div>
          <LeakFunnel steps={data.steps} clicks={t.clicks} leak={data.biggest_leak} />
        </>
      )}
    </div>
  );
}
